import React, { useMemo, useState, useEffect } from 'react';
import { createMemoStyles, defaultBaseline, defaultFonts, uniqueId, safeRest } from '@wds/utils';
import { Button } from '@wds/button';
import { Input } from '@wds/input';
import { InputGroup, InputGroupAddon } from '@wds/input-group';
import { IconMinus, IconPlus } from '@wds/icons';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function () {
  __assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
function __rest(s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}
var useStyles = createMemoStyles(function (theme) {
  var numberPickerStyles = {
    wrenchNumberPicker: function (_a) {
      var stretched = _a.stretched;
      return {
        display: stretched ? 'block' : 'inline-block',
        "& input[type='number']": {
          '-webkit-appearance': 'textfield'
        },
        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
          '-webkit-appearance': 'none',
          margin: 0
        }
      };
    },
    wrenchNumberPickerInput: function () {
      return {
        ' & > input': {
          display: 'inline-flex',
          justifyContent: 'center',
          alignItems: 'center',
          minWidth: '3em',
          textAlign: 'center',
          border: 'none'
        }
      };
    },
    wrenchNumberPickerAddonButton: function () {
      return __assign(__assign(__assign({}, defaultBaseline), defaultFonts(theme)), {
        '& [class^="wrenchIcon"]': {
          marginRight: 0,
          marginLeft: 0
        }
      });
    }
  };
  return numberPickerStyles;
});

/**
 * Adjusts the provided value to fit between (optional) min and max constraints, and ensures that it
 * is a proper whole number
 * @param {Number|String} val value to adjust
 * @param {Number} min min value (optional)
 * @param {Number} max max value (optional)
 * @returns Number
 */
var adjustValue = function (value, min, max) {
  var initialValue = Number(value !== null && value !== void 0 ? value : '');
  if (value < min) initialValue = min;else if (max && value > max) initialValue = max;
  return Math.round(initialValue);
};
var NumberPicker = function (_a) {
  var _b = _a.color,
    color = _b === void 0 ? undefined : _b,
    _c = _a.label,
    label = _c === void 0 ? undefined : _c,
    _d = _a.inputID,
    inputID = _d === void 0 ? undefined : _d,
    _e = _a.max,
    max = _e === void 0 ? Infinity : _e,
    _f = _a.min,
    min = _f === void 0 ? 0 : _f,
    _g = _a.onChange,
    onChange = _g === void 0 ? undefined : _g,
    _h = _a.disabled,
    disabled = _h === void 0 ? false : _h,
    _j = _a.step,
    step = _j === void 0 ? 1 : _j,
    _k = _a.stretched,
    stretched = _k === void 0 ? false : _k,
    _l = _a.value,
    value = _l === void 0 ? 0 : _l,
    rest = __rest(_a, ["color", "label", "inputID", "max", "min", "onChange", "disabled", "step", "stretched", "value"]);
  var _m = useStyles({
      stretched: stretched
    }),
    wrenchNumberPicker = _m.wrenchNumberPicker,
    wrenchNumberPickerInput = _m.wrenchNumberPickerInput,
    wrenchNumberPickerAddonButton = _m.wrenchNumberPickerAddonButton;
  var uniqueInputID = useMemo(function () {
    return inputID !== null && inputID !== void 0 ? inputID : uniqueId('input');
  }, [inputID]);
  /// //////////////// STATES ///////////////////
  var _o = useState(false),
    keyInputActive = _o[0],
    setKeyInputActive = _o[1];
  var _p = useState(adjustValue(value, min, max)),
    stateValue = _p[0],
    setValue = _p[1];
  var shouldDisableMinus = stateValue <= min;
  var shouldDisablePlus = max && stateValue >= max;
  /// //////////////// EFFECTS ///////////////////
  useEffect(function () {
    setValue(adjustValue(value, min, max));
  }, [value, min, max]);
  /// //////////////// Handlers ///////////////////
  var handleChange = function (v) {
    if (onChange) {
      onChange(v);
    }
  };
  var handleMinus = function () {
    var val = stateValue - step;
    var newValue = val < min ? min : val;
    handleChange(newValue);
    setValue(newValue);
  };
  var handlePlus = function () {
    var val = stateValue + step;
    var newValue = max && val > max ? max : val;
    handleChange(newValue);
    setValue(newValue);
  };
  var handleInputChange = function (e) {
    var rawValue = Number(e.target.value);
    // we keep the input's raw value in our internal state
    setValue(rawValue);
    if (!keyInputActive) {
      handleChange(adjustValue(rawValue, min, max));
    }
  };
  var handleInputBlur = function (e) {
    // if user has been typing, resume onChange handling
    if (keyInputActive) {
      setKeyInputActive(false);
      var adjusted = adjustValue(e.target.value, min, max);
      setValue(adjusted);
      handleChange(adjusted);
    }
  };
  var handleKeyDown = function (e) {
    // if user starts typing numbers, pause onChange handling until blur
    if (!Number.isNaN(Number(e.key)) || e.key === 'Backspace') {
      setKeyInputActive(true);
    }
  };
  return React.createElement(React.Fragment, null, React.createElement("div", {
    className: wrenchNumberPicker,
    onKeyDown: handleKeyDown
  }, React.createElement(InputGroup, {
    label: label,
    id: uniqueInputID,
    stretched: stretched,
    color: color,
    disabled: disabled
  }, React.createElement("div", {
    className: wrenchNumberPickerAddonButton
  }, React.createElement(InputGroupAddon, null, React.createElement(Button, {
    size: "sm",
    color: "muted",
    onClick: handleMinus,
    disabled: shouldDisableMinus || disabled,
    label: "Decrease value by ".concat(step),
    name: "decrement"
  }, React.createElement(IconMinus, {
    title: "Decrement Value"
  })))), React.createElement("div", {
    className: wrenchNumberPickerInput
  }, React.createElement(Input, __assign({
    ariaLabel: label,
    id: uniqueInputID,
    inputSize: 3,
    value: Number(stateValue).toString(),
    onChange: handleInputChange,
    onBlur: handleInputBlur,
    type: "number",
    min: min,
    max: max,
    step: step,
    disabled: disabled
  }, safeRest(rest)))), React.createElement("div", {
    className: wrenchNumberPickerAddonButton
  }, React.createElement(InputGroupAddon, null, React.createElement(Button, {
    role: "button",
    size: "sm",
    color: "muted",
    onClick: handlePlus,
    disabled: shouldDisablePlus || disabled,
    label: "Increase value by ".concat(step)
  }, React.createElement(IconPlus, {
    title: "Increment Value"
  })))))));
};
export { NumberPicker };